import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Save, FileText, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = [
  { key: 'sow', label: 'Scope of Work' },
  { key: 'approval_letter', label: 'Approval Letters' },
];

export default function TemplateLibrary() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('sow');
  const [editing, setEditing] = useState(null);
  const [body, setBody] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/buyer/templates`, { withCredentials: true });
      setItems(data || []);
    } catch (e) { toast.error('Failed to load templates'); }
  };
  useEffect(() => { load(); }, []);

  const open = (t) => { setEditing(t); setBody(t.body || {}); };
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/buyer/templates/${editing.id}`, { body }, { withCredentials: true });
      toast.success('Template saved');
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const filtered = items.filter(i => i.type === tab);

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-navy text-white">
        <nav className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/buyer-dashboard')} className="text-white/70 hover:text-white inline-flex items-center" data-testid="tpl-back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </button>
          <div className="font-bold flex items-center gap-2"><BookOpen className="h-5 w-5 text-gold" /> Template Library</div>
          <div />
        </nav>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-5xl">
        <div className="flex gap-2 mb-4 border-b border-border">
          {TYPES.map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setEditing(null); }}
              className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${tab === t.key ? 'border-orange text-orange' : 'border-transparent text-muted-foreground hover:text-navy'}`}
              data-testid={`tpl-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {!editing && (
          <div className="grid sm:grid-cols-2 gap-3">
            {filtered.map(t => (
              <div key={t.id} className="bg-card border border-border rounded-lg p-4" data-testid={`tpl-card-${t.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bold text-navy text-sm">{t.name}</div>
                  <span className="text-[10px] uppercase text-muted-foreground">v{t.version || 1}</span>
                </div>
                <div className="text-xs text-muted-foreground line-clamp-3 mb-3">
                  {t.body?.scope_of_work || t.body?.text || JSON.stringify(t.body).slice(0, 140)}
                </div>
                <button onClick={() => open(t)} className="px-3 py-1.5 text-xs font-bold rounded bg-navy text-white inline-flex items-center" data-testid={`tpl-edit-${t.id}`}>
                  <Edit3 className="h-3 w-3 mr-1" /> Edit Template
                </button>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="bg-card border border-border rounded-lg p-5" data-testid="tpl-editor">
            <div className="flex items-center justify-between mb-4">
              <div className="font-bold text-navy text-lg flex items-center gap-2"><FileText className="h-4 w-4" /> Editing: {editing.name}</div>
              <button onClick={() => setEditing(null)} className="text-xs underline text-muted-foreground">Cancel</button>
            </div>
            <div className="space-y-3">
              {Object.entries(body).filter(([k]) => k !== 'placeholders').map(([k, v]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-navy mb-1 capitalize">{k.replace(/_/g, ' ')}</label>
                  <textarea value={v || ''} onChange={(e) => setBody({ ...body, [k]: e.target.value })}
                    rows={k === 'text' || k === 'scope_of_work' ? 8 : 3}
                    className="w-full px-3 py-2 rounded border border-input bg-background text-sm font-mono"
                    data-testid={`tpl-field-${k}`} />
                </div>
              ))}
              <button onClick={save} disabled={saving} className="px-5 py-2 rounded bg-orange text-white text-sm font-bold disabled:opacity-40 inline-flex items-center" data-testid="tpl-save-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save Template'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
